import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Developer } from '../../utils/types';
import { MapPinIcon, CheckCircleIcon, GithubIcon, ExternalLinkIcon } from 'lucide-react';
interface TalentCapsuleCardProps {
  developer: Developer;
  featured?: boolean;
}
export const TalentCapsuleCard: React.FC<TalentCapsuleCardProps> = ({
  developer,
  featured = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const getCountryFlag = (country: string) => {
    const countryFlags: {
      [key: string]: string;
    } = {
      'United States': '🇺🇸',
      Uganda: '🇺🇬',
      Germany: '🇩🇪',
      Philippines: '🇵🇭',
      Egypt: '🇪🇬',
      Italy: '🇮🇹',
      Romania: '🇷🇴',
      France: '🇫🇷'
    };
    return countryFlags[country] || '🏳️';
  };
  const getSkillIcon = (skill: string) => {
    const skillIcons: {
      [key: string]: string;
    } = {
      Laravel: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6raD3sQbYhrAYJrpu7Fmsg/Laravel.jpg',
      Python: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rmryh1szxRZK8Ckh29GLMA/python.png',
      React: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ptv5Wif4YhmkFBsFHL24ST/React.png',
      Symfony: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7M5FR1J9g2nFRdnfNT5c1d/Symfony.png',
      'Vue.js': 'https://uploadthingy.s3.us-west-1.amazonaws.com/ncaFrZrfpfapyV9ExA35DY/Vue_logo.png',
      PHP: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/27/PHP-logo.svg/1280px-PHP-logo.svg.png',
      JavaScript: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6a/JavaScript-logo.png/800px-JavaScript-logo.png'
    };
    return skillIcons[skill] || `https://via.placeholder.com/30?text=${skill.charAt(0)}`;
  };
  const statusColors = {
    available: 'bg-green-100 text-green-800',
    unavailable: 'bg-gray-100 text-gray-800',
    looking: 'bg-blue-100 text-blue-800'
  };
  const safeStatus = developer.status || 'unavailable';
  const statusColor = statusColors[safeStatus as keyof typeof statusColors] || statusColors.unavailable;
  const statusText = safeStatus === 'available' ? 'Available now' : safeStatus.charAt(0).toUpperCase() + safeStatus.slice(1);
  return <div className={`bg-white rounded-2xl overflow-hidden transition-all duration-300 ${featured ? 'md:col-span-2' : ''} ${isHovered ? 'shadow-lg transform -translate-y-1' : 'shadow-sm'}`} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-32">
      <div className="p-8" data-id="element-33">
        {/* Header */}
        <div className="flex items-start gap-4 mb-6" data-id="element-34">
          <div className="relative flex-shrink-0" data-id="element-35">
            <img src={developer.avatar} alt={developer.name} className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md" data-id="element-36" />
            {developer.status === 'available' && <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white" data-id="element-37"></div>}
          </div>
          <div className="flex-1 min-w-0" data-id="element-38">
            <div className="flex items-start justify-between gap-2 mb-3" data-id="element-39">
              <div data-id="element-40">
                <h3 className="text-lg font-semibold text-gray-900 truncate" data-id="element-41">
                  {developer.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1" data-id="element-42">
                  {developer.position} {developer.role}
                </p>
              </div>
              <div className="flex items-center gap-1" data-id="element-43">
                <span className="text-lg" data-id="element-44">
                  {getCountryFlag(developer.country)}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-45">
              <span className={`text-xs font-medium px-3 py-1.5 rounded-full ${statusColor}`} data-id="element-46">
                {statusText}
              </span>
              <div className="flex items-center gap-1 text-xs text-gray-500" data-id="element-47">
                <CheckCircleIcon className="h-3 w-3 text-[#d73b3e]" data-id="element-48" />
                <span className="font-medium" data-id="element-49">Verified</span>
              </div>
            </div>
          </div>
        </div>
        {/* Bio */}
        <p className="text-sm text-gray-600 mb-6 leading-relaxed line-clamp-2" data-id="element-50">
          {developer.bio}
        </p>
        {/* Skills */}
        <div className="flex flex-wrap gap-2 mb-6" data-id="element-51">
          {developer.skills.slice(0, 5).map(skill => <div key={skill.name} className="flex items-center gap-1.5 bg-gray-50 px-3 py-2 rounded-full hover:bg-gray-100 transition-colors duration-200" data-id="element-52">
              <img src={getSkillIcon(skill.name)} alt={skill.name} className="w-4 h-4 object-contain" data-id="element-53" />
              <span className="text-xs font-medium text-gray-700" data-id="element-54">
                {skill.name}
              </span>
            </div>)}
          {developer.skills.length > 5 && <div className="flex items-center justify-center bg-gray-50 px-3 py-2 rounded-full" data-id="element-55">
              <span className="text-xs font-medium text-gray-700" data-id="element-56">
                +{developer.skills.length - 5}
              </span>
            </div>}
        </div>
        {/* Expanded content on hover */}
        <div className={`transition-all duration-300 overflow-hidden ${isHovered ? 'max-h-32 opacity-100 mb-6' : 'max-h-0 opacity-0'}`} data-id="element-57">
          <div className="pt-6 border-t border-gray-100" data-id="element-58">
            <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-59">
              <div className="flex items-center gap-1.5" data-id="element-60">
                <MapPinIcon className="h-3.5 w-3.5" data-id="element-61" />
                <span data-id="element-62">{developer.location}</span>
              </div>
              {developer.links.github && <a href={developer.links.github} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-gray-900 transition-colors" onClick={e => e.stopPropagation()} data-id="element-63">
                  <GithubIcon className="h-3.5 w-3.5" data-id="element-64" />
                  <span data-id="element-65">GitHub</span>
                </a>}
              {developer.links.website && <a href={developer.links.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-gray-900 transition-colors" onClick={e => e.stopPropagation()} data-id="element-66">
                  <ExternalLinkIcon className="h-3.5 w-3.5" data-id="element-67" />
                  <span data-id="element-68">Website</span>
                </a>}
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-100" data-id="element-69">
          <div className="flex items-baseline gap-1" data-id="element-70">
            <span className="text-lg font-bold text-gray-900" data-id="element-71">
              €{developer.rate.min}–€{developer.rate.max}
            </span>
            <span className="text-sm text-gray-500" data-id="element-72">/hr</span>
          </div>
          <div className="flex gap-3" data-id="element-73">
            <button className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 rounded-full hover:bg-gray-100 transition-colors duration-200" data-id="element-74">
              Shortlist
            </button>
            <Link to={`/developers/${developer.id}`} className="px-5 py-2.5 text-sm font-medium text-white bg-[#d73b3e] rounded-full hover:bg-[#c02f32] transition-colors duration-200 shadow-sm" data-id="element-75">
              View profile
            </Link>
          </div>
        </div>
      </div>
    </div>;
};